import React, { useState } from 'react'
import styled from 'styled-components'
import get from 'lodash/get'
import { FormattedMessage } from 'react-intl'

import { media } from 'utils'
import { useDeviceSize, useAccordion, useOpenModalFromEvent } from 'hooks'

import { Container, Row, Col } from 'components/Grid'
import { Section, Select, PolyvVideo } from 'components/Common'
import Slider from 'components/Slider'
import HubSpotFormModal from '../EventFormModal'

import EventCard from '../EventCard'

import Img from 'gatsby-image'
import { getFluidGatsbyImage } from 'gatsby-plugin-storyblok-image'

import plusIconDark from 'static/img/plus_icon_dark.svg'
import minusIconDark from 'static/img/minus_dark.svg'

import { VideoDiv, ViewDetailsButton, Expandable } from './styles'
import SbEditable from 'storyblok-react'

import WithEvents from '../WithEvents'

const IsChinaDeploy = process.env.GATSBY_CHINA_DEPLOY

function EventsSectionAccordion({ blok, allEvents, lang }) {
  const { isMobile, isTablet } = useDeviceSize()
  const [ref, toggleAccordion, height] = useAccordion()
  const { modalOpen, toggleModalVisibility } = useOpenModalFromEvent()
  const [filter, setFilter] = useState(null)
  const response = !IsChinaDeploy
    ? get(blok, 'video.vimeo_oembed.response')
    : blok.video_alt

  function takePageFilter(obj) {
    const pageFilteredEvents = obj.filter(val => {
      return val.content.grade_level === filter
    })
    if (height && pageFilteredEvents.length < 1) toggleAccordion()
    return pageFilteredEvents
  }

  const events = (!filter && allEvents) || takePageFilter(allEvents)
  const eventsWithModals = events.filter(event => event.content.signup_form)

  const imageProps =
    blok.image &&
    getFluidGatsbyImage(blok.image, {
      maxWidth: 555,
      maxHeight: 281,
    })

  /* Methods */
  const renderEvent = props =>
    <EventContent
      {...props}
      locale={lang}
      no_location={Boolean(blok.no_location)}
      key={props.uuid}
      toggleModalVisibility={toggleModalVisibility}
    />

  return (
    <SbEditable content={blok}>
      <Section
        paddingTop={blok.padding_top || 106}
        paddingBottom={blok.padding_bottom || 64}
        paddingTopMb={blok.padding_top_mobile || 120}
        paddingBottomMb={blok.padding_bottom_mobile || 80}
        css="background-color: transparent;"
      >
        <Container>
          <Row>
            <Col width={[1, 1, 1, 1 / 2]}>
              <h4>{blok.heading}</h4>
              <p css="margin-bottom: 15px;">{blok.paragraph}</p>
              {!isTablet && (
                <>
                  {blok.filter_by_grades && (
                    <SelectWrapper>
                      <Select
                        options={blok.filter_by_grades_options}
                        onSelect={setFilter}
                        defaultValue={blok.filters_label}
                      />
                    </SelectWrapper>
                  )}
                  <ViewDetailsButton
                    onClick={toggleAccordion}
                    isExpanded={!height}
                    isMargin={blok.filter_by_grades}
                    isEvents={!events.length < 1}
                  >
                    <FormattedMessage id="upcoming dates" />
                    <img
                      src={height ? minusIconDark : plusIconDark}
                      alt={`${height ? 'retract icon' : 'expand icon'}`}
                      css={`
                        width: 17px;
                        height: 17px;
                      `}
                    />
                  </ViewDetailsButton>
                </>
              )}
            </Col>

            {response && (
              <Col width={[1, 1, 1, 1 / 2]}>
                {!IsChinaDeploy ? (
                  <VideoDiv
                    dangerouslySetInnerHTML={{ __html: response.html }}
                  />
                ) : (
                  <PolyvVideo videoId={response} />
                )}
              </Col>
            )}
            {imageProps && blok.image && (
              <Col width={[1, 1, 1, 1 / 2]}>
                <Img fluid={imageProps} />
              </Col>
            )}

            {isTablet && (
              <Col>
                {blok.filter_by_grades && (
                  <SelectWrapper>
                    <Select
                      options={blok.filter_by_grades_options}
                      onSelect={setFilter}
                      defaultValue={blok.filters_label}
                    />
                  </SelectWrapper>
                )}
                <ViewDetailsButton
                  onClick={toggleAccordion}
                  isExpanded={!height}
                  isMargin={blok.filter_by_grades}
                  isEvents={!events.length < 1}
                >
                  <FormattedMessage id="upcoming dates" />
                  <img
                    src={height ? minusIconDark : plusIconDark}
                    alt={`${height ? 'retract icon' : 'expand icon'}`}
                    css={`
                      width: 17px;
                      height: 17px;
                    `}
                  />
                </ViewDetailsButton>
              </Col>
            )}
          </Row>
        </Container>

        <Expandable
          ref={ref}
          style={{
            transition: 'height .4s ease-in-out',
            height: `${height || 0}px`,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Container>
            <Row>
              {events.length > 2 ? (
                <Col width={[1]} px={0}>
                  <Slider
                    isMobile={isMobile}
                    setting={{
                      ...(isMobile ? { slidesToShow: 1 } : { slidesToShow: 3 }),
                      equalItemsHeight: true,
                    }}
                    nextArrowCss="top: 32px !important;"
                    prevArrowCss={
                      isMobile
                        ? 'top: 32px !important; left: 90px !important; z-index: 10;'
                        : 'top: 32px !important; z-index: 10;'
                    }
                  >
                    {events.map(renderEvent)}
                  </Slider>
                </Col>
              ) : (
                events.map((el, i) => {
                  return (
                    <ColEventWrapper
                      key={`event_element__${i}`}
                      width={[1, 1, 1 / 2, 1 / 3]}
                      px={0}
                    >
                      {renderEvent(el)}
                    </ColEventWrapper>
                  )
                })
              )}
            </Row>
          </Container>
        </Expandable>
      </Section>

      {eventsWithModals.length > 0 && eventsWithModals.map(event => event.uuid === modalOpen && (
        <HubSpotFormModal
          handleCloseModal={toggleModalVisibility}
          content={event.content}
          key={event.id}
        />
      ))}
    </SbEditable>
  )
}

export default WithEvents(EventsSectionAccordion)

const ColEventWrapper = styled(Col)`
  display: flex;
`

const SelectWrapper = styled.div`
  max-width: 320px;
  margin: 15px 0;
  * {
    font-size: 16px;
  }
`

const EventContent = styled(EventCard)`
  padding: 61px 23px 62px 25px;
  background-color: #fff;
  z-index: 10;
  display: flex !important;
  margin: auto;
  margin-top: 60px;
  margin-bottom: 0;
  border: 1px solid ${({ theme }) => theme.colors.neutral};
  max-width: 360px;
  min-height: 360px;

  ${media.greaterThan('350px')`
    padding: 61px 23px 62px 25px;
  `}
  ${media.greaterThan('450px')`
    padding: 61px 23px 62px 25px;
  `}
  ${media.greaterThan('770px')`
    padding: 70px 0 77px 10px;
    max-width: 240px;
  `}
  ${media.greaterThan('928px')`
    max-width: 260px;
  `}
  ${media.greaterThan('lg')`
    padding: 70px 32px 77px 60px;
    margin-top: 70px;
    max-width: 360px;
  `}
`
